'use strict';

const throttle = require('../util/throttle');
const urlTools = require('../util/url');
const measures = require('../util/measures');

var selectors = {
    mainContainer: '.js-search-results',
    gridContainer: '.product-grid',
    showMoreButton: '.show-more button',
    showMoreButtonContainer: '.grid-footer',
    showPreviousButton: '.show-previous button',
    showPreviousButtonContainer: '.grid-previous-header',
    pgpTitle: '.pgptiles',
    swatches: '.color-swatches',
    nonMarkedProduct: '.pgptiles:not([data-item-paging-info])'
};

var attributes = {
    usePagingOnReturn: 'data-paging-on-return',
    maxScrollLoadedPages: 'data-max-scroll-loaded-pages',
    maxScrollLoadedPagesMobile: 'data-max-scroll-loaded-pages-mobile',
    itemPagingInfo: 'data-item-paging-info',
    itemID: 'data-pid'
};

var requestStates = {
    pending: 'pending',
    loaded: 'loaded',
    error: 'error'
};

var lastClickedProductKey = 'lastClickedProduct';

var searchGrid = {

    // Methods for public use.

    init: function () {
        this.requestData = {};
        this.initTime = Date.now();

        this.isSafari = (/iphone|ipad/i).test(navigator.userAgent || '')
            || (/apple/i).test(navigator.vendor || '');

        this.scrollY = window.pageYOffset;
        this.scrollAddedPages = 0;

        this.scrollListener = throttle(this.handleScroll.bind(this), 75);
        this.clickListener = this.handleClick.bind(this);

        this.setItemsPagingInfo(window.location.href);
        this.setScrolledStateOnInit(this.scrollY);
        this.returnToClickedProduct();
        this.setListeners(true);

        // eslint-disable-next-line no-underscore-dangle
        window.__searchGrid = this; // Just for debugging.
    },

    update: function (scrollAdjusted) {
        this.cancelRequests();

        this.scrollAddedPages = 0;
        this.setItemsPagingInfo(window.location.href);

        if (!scrollAdjusted && window.scrollY > 50) {
            this.scrollAddedPages = 1000000;
            this.showLoadMoreContainer(document);
        }
    },

    cancelRequests() {
        var requestData = this.requestData;

        for (var i = 0, keys = Object.keys(requestData); i < keys.length; i += 1) {
            var key = keys[i];
            var value = requestData[key];
            value.cancelled = true;

            try {
                if (value.state === requestStates.pending) {
                    value.xhr.onreadystatechange = function () {};
                    value.xhr.abort();
                }
            } catch (e) {
                // Just to be sure.
            }
        }

        this.requestData = {};
    },

    getPage: function (url, direction) {
        var request = this.loadPage(url, direction);

        if (!request) {
            return null;
        }

        if (request.direction === 1) {
            this.scrollAddedPages = 0;
        }

        return request.deferred.promise();
    },

    hasPage: function (url) {
        if (!url) {
            return false;
        }

        var data = this.requestData[url];

        if (!data) {
            return false;
        }

        return data.state === requestStates.loaded;
    },

    isPagingEnabled: function () {
        return this.getConfig().usePagingOnReturn;
    },

    // Methods used on init.

    setScrolledStateOnInit(scrollY) {
        if (scrollY < 50) {
            return;
        }

        this.scrollAddedPages = 1000000;
        this.showLoadMoreContainer(document);
    },

    showLoadMoreContainer(container) {
        var loadMoreContainer = container.querySelector(selectors.showMoreButtonContainer);

        if (!loadMoreContainer || loadMoreContainer.classList.contains('grid-footer-active')) {
            return;
        }

        loadMoreContainer.classList.add('grid-footer-active');
    },

    setItemsPagingInfo: function (url, container) {
        var searchParams = urlTools.getSearchParams(url);
        var start = searchParams.start || '';
        var sz = searchParams.sz || '';

        var numeric = /^\d+$/;
        var zero = /^0+$/;

        var bothValid = numeric.test(start)
            && numeric.test(sz)
            && !zero.test(sz);

        var store = {
            start: bothValid ? start : '',
            sz: bothValid ? sz : ''
        };

        var storeJSON = JSON.stringify(store);
        var useContainer = container || document;
        var products = useContainer.querySelectorAll(selectors.nonMarkedProduct);

        for (var i = 0; i < products.length; i += 1) {
            var product = products[i];

            product.setAttribute(attributes.itemPagingInfo, storeJSON);
        }
    },

    returnToClickedProduct() {
        var lastClickedInfo = null;

        try {
            var json = window.sessionStorage.getItem(lastClickedProductKey);
            if (json) {
                window.sessionStorage.removeItem(lastClickedProductKey);
                lastClickedInfo = json ? JSON.parse(json) : null;
            }
        } catch (e) {
            // Just in case. With standard browser settings this can not occur.
        }

        if (!lastClickedInfo
            || typeof lastClickedInfo !== 'object'
            || typeof lastClickedInfo.productId !== 'string') {
            return;
        }

        var productElement = null;

        try {
            productElement = document.querySelector('[data-pid="' + lastClickedInfo.productId + '"]');
        } catch (e) {
            return;
        }

        if (!productElement) {
            return;
        }

        var header = document.querySelector('.header');
        var headerHeight = header ? header.getBoundingClientRect().height : 0;
        var scrollY = productElement.getBoundingClientRect().top - headerHeight - 60;

        this.scrollToClickedProduct(scrollY);
    },

    scrollToClickedProduct(scrollY) {
        const performScroll = () => {
            window.scrollTo(0, scrollY);
            this.handleScroll();
        };

        if (!this.isSafari) {
            performScroll();
        } else {
            // Work around for unwanted behavior in Safari.
            window.history.scrollRestoration = 'manual';
            setTimeout(performScroll, 100);
        }
    },

    // Click handling.

    handleClick: function (event) {
        var usePagingOnReturn = this.getConfig().usePagingOnReturn;

        if (!usePagingOnReturn) {
            return;
        }

        var linkElement = event.target.closest('a');
        var linkHref = linkElement ? linkElement.getAttribute('href') : null;

        if (!linkElement || !linkHref) {
            return;
        }

        if (linkElement.closest(selectors.swatches)
            && this.isMobile()) {
            return;
        }

        var infoElement = event.target.closest('[' + attributes.itemPagingInfo + ']');
        var infoJSON = infoElement ? infoElement.getAttribute(attributes.itemPagingInfo) : null;

        if (!infoJSON) {
            return;
        }

        var info = null;
        try {
            info = JSON.parse(infoJSON);
        } catch (e) {
            return;
        }

        var productIdElement = infoElement.querySelector('[' + attributes.itemID + ']');
        var productId = productIdElement ? productIdElement.getAttribute(attributes.itemID) : null;

        if (!productId) {
            return;
        }

        if (this.isOpeningNewTab(event)) {
            return;
        }

        var error = null;

        try {
            var url = this.createPagingUrl(info);
            window.history.replaceState({}, document.title, url);
        } catch (e) {
            error = e;
        }

        if (error) {
            return;
        }

        var store = {
            pagingInfo: info,
            productId: productId
        };

        try {
            window.sessionStorage.setItem(lastClickedProductKey, JSON.stringify(store, null, 2));
        } catch (e) {
            // Just in case, under standard browser settings this should always work.
        }

        event.preventDefault();

        // Work around for unwanted behavior in Safari.
        window.history.scrollRestoration = this.isSafari ? 'auto' : 'manual';

        setTimeout(function () {
            window.location.href = linkHref;
        }, 5);
    },

    createPagingUrl(info) {
        var url = new URL(window.location.href);

        if (info.start && info.sz && info.start !== '0') {
            url.searchParams.set('start', info.start);
            url.searchParams.set('sz', info.sz);
        } else {
            url.searchParams.delete('start');
            url.searchParams.delete('sz');
        }

        return url.toString();
    },

    // Scroll handling.

    handleScroll: function () {
        var newScrollY = window.pageYOffset;
        var direction = newScrollY > this.scrollY ? 1 : -1;

        if (direction === 1 && Date.now() - this.initTime < 1250) {
            this.setScrolledStateOnInit(newScrollY);
        }

        var loadSelector = direction === 1 ? selectors.showMoreButton : selectors.showPreviousButton;
        var loadButton = document.querySelector(loadSelector);
        var url = loadButton ? loadButton.getAttribute('data-url') : null;
        this.scrollY = newScrollY;

        if (!url) {
            return;
        }

        this.scrollLoadPageIfAllowed(this.loadPage(url, direction));
    },

    scrollLoadPageIfAllowed(pageRequest) {
        if (!pageRequest) {
            return;
        }

        if (pageRequest.direction !== 1 || pageRequest.scrollLoading) {
            return;
        }

        var maxScrollLoadedPages = this.getConfig().maxScrollLoadedPages;

        if (this.scrollAddedPages >= maxScrollLoadedPages) {
            this.showLoadMoreContainer(document);
            return;
        }

        var that = this;

        // eslint-disable-next-line no-param-reassign
        pageRequest.scrollLoading = true;
        pageRequest.deferred.promise().done(function (response) {
            that.dispatchScrollAdd(response);
        }).fail(function (error) {
            if (window.console) {
                window.console.error('error on scroll load request: ', error);
            }
        });
    },

    dispatchScrollAdd(response) {
        if (typeof this.onScrollAdd === 'function') {
            response.scrollLoaded = true;
            this.scrollAddedPages += 1;
            this.onScrollAdd(response, true);
        }
    },

    loadPage: function (url, direction) {
        if (!url) {
            return null;
        }

        var existingRequest = this.requestData[url];

        if (existingRequest) {
            return existingRequest;
        }

        var request = {
            url: url,
            direction: direction,
            state: requestStates.pending,
            cancelled: false,
            scrollLoading: false,
            responseText: null,
            responseObject: null,
            deferred: $.Deferred(),
            xhr: null
        };

        request.xhr = new XMLHttpRequest();
        request.xhr.onreadystatechange = this.handleData.bind(this, request);
        request.xhr.open('GET', request.url, true);
        request.xhr.send();

        this.requestData[url] = request;

        return request;
    },

    handleData: function (request) {
        if (request.cancelled) {
            return; // To be sure.
        }

        if (request.xhr.readyState !== 4) {
            return;
        }

        if (request.xhr.status !== 200) {
            request.state = requestStates.error;
            request.deferred.reject(new Error('Invalid status'));
            return;
        }

        request.responseText = request.xhr.responseText;
        request.responseObject = this.createResponseObject(request);
        request.state = requestStates.loaded;
        request.deferred.resolve(request.responseObject);
    },

    createResponseObject: function (request) {
        var div = document.createElement('div');
        div.innerHTML = request.responseText;

        // Get footer (before we possibly remove it).
        var footer = div.querySelector(selectors.showMoreButtonContainer);
        var footerHTML = footer ? footer.outerHTML : '';

        // Set paging info on items.
        this.setItemsPagingInfo(request.url, div);

        var $replaceElement = request.direction === 1 ? $(selectors.showMoreButtonContainer) : $(selectors.showPreviousButtonContainer);
        var html = request.direction === 1 ? this.prepareNextPage(div) : this.preparePreviousPage(div);

        return {
            direction: request.direction,
            $replaceElement: $replaceElement,
            footerHTML: footerHTML, // Adding footer HTML for compatibility with search.js
            html: html
        };
    },

    prepareNextPage: function (div) {
        // Always remove the show previous block on a next page.
        var showPrevious = div.querySelector(selectors.showPreviousButtonContainer);

        if (showPrevious) {
            showPrevious.parentNode.removeChild(showPrevious);
        }

        // Return resulting HTML.
        return div.innerHTML;
    },

    preparePreviousPage: function (div) {
        // Always remove the show next block on a prev page.
        var showNext = div.querySelector(selectors.showMoreButtonContainer);

        if (showNext) {
            showNext.parentNode.removeChild(showNext);
        }

        // Disable the order property on items.
        var tiles = div.querySelectorAll(selectors.pgpTitle);

        for (var i = 0; i < tiles.length; i += 1) {
            var tile = tiles[i];
            tile.style.order = '';
        }

        // Return resulting HTML.
        return div.innerHTML;
    },

    // Assistants.

    isOpeningNewTab(event) {
        const isCtrlPressed = event.ctrlKey;
        const isMetaKeyPressed = event.metaKey;

        return !!(isCtrlPressed || isMetaKeyPressed);
    },

    getConfig: function () {
        var mainContainer = document.querySelector(selectors.mainContainer);

        var result = {
            usePagingOnReturn: false,
            maxScrollLoadedPages: 0
        };

        if (!mainContainer) {
            return result;
        }

        var maxScrollLoadedAttribute = this.isMobile() ? attributes.maxScrollLoadedPagesMobile : attributes.maxScrollLoadedPages;

        result.usePagingOnReturn = mainContainer.getAttribute(attributes.usePagingOnReturn) === 'true';
        result.maxScrollLoadedPages = parseInt(mainContainer.getAttribute(maxScrollLoadedAttribute), 10) || 0;

        return result;
    },

    setListeners: function (activate) {
        var method = activate ? 'addEventListener' : 'removeEventListener';
        var productGrid = document.querySelector(selectors.gridContainer);

        window[method]('scroll', this.scrollListener);

        if (productGrid) {
            productGrid[method]('click', this.clickListener);
        }
    },

    isMobile() {
        return measures.isMobile();
    }

};

module.exports = searchGrid;
